<template>
  <ShippingOption
    v-for="choice in choices"
    :key="choice.shippingId"
    :checked="choice.selected"
    class="mt-12 first:mt-0"
    :earliest-arrival-date="choice.earliestArrivalDate"
    :is-collection-point="choice.isCollectionPoint"
    :is-free="choice.price === 0"
    :last-collection-point
    :latest-arrival-date="choice.latestArrivalDate"
    :listing-id
    :price="getFormattedPrice(choice)"
    :shipper="choice.shipperDisplay"
    :shipping-id="choice.shippingId"
    :show-collection-point-error
    @change="checkOption(option, choice)"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { Currency } from '@backmarket/http-api'
import type {
  ShippingChoice as ShippingChoiceType,
  ShippingOption as ShippingOptionType,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import ShippingOption from '../ShippingOption/ShippingOption.vue'

const emit = defineEmits(['select'])

const props = defineProps<{
  listingId: string
  option: ShippingOptionType
  showCollectionPointError: boolean
  lastCollectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
}>()

const experiments = useExperiments()
const i18n = useI18n()

const collectionPointDisabled = computed(() => {
  return (
    experiments['experiment.checkoutCollectionPointOption'] ===
    'collectionPointDisabled'
  )
})

const choices = ref(props.option.choices)

const getFormattedPrice = (choice: ShippingChoiceType) => {
  if (choice.price === 0) {
    return choice.priceWithCurrency
  }

  return i18n.price({
    amount: `${choice.price}`,
    currency: choice.currency as Currency,
  })
}

const checkOption = async (
  option: ShippingOptionType,
  choice: ShippingChoiceType,
) => {
  choices.value = choices.value.map((c) => {
    return {
      ...c,
      selected: choice.shippingId === c.shippingId,
    }
  })

  emit(
    'select',
    {
      listingId: props.listingId,
      optionType: option.type,
      optionId: choice.shippingId,
    },
    choice,
  )
}

onMounted(() => {
  choices.value = props.option.choices.filter((choice) => {
    if (!choice.isCollectionPoint) return true

    return choice.selected || !collectionPointDisabled.value
  })

  if (choices.value.every((choice) => !choice.selected)) {
    checkOption(props.option, props.option.choices[0])
  }
})
</script>
